import React, { useCallback, useEffect, useState } from 'react';
import { Button, Table } from '@ds/ui';
import BannerInfo from '../../types/BannerInfo';
import { apiFetch, handleApiError } from '../../utils/apiClient';

interface NamedKeyInternal {
  name: string;
  keyType: number;
  pendingIds: string[];
  pendingCount: number;
  currentIds: string[];
  currentCount: number;
  activeIds: string[];
  activeCount: number;
  error: string | null;
}
interface NamedKeysProps {
  onBannerUpdate: (bannerInfo: BannerInfo | null) => void,
  onNamedKeysChange: (keys: string) => void
}

interface State {
  namedKeys: NamedKeyInternal[];
  loading: boolean;
}

export const NamedKeysInternal: React.FC<NamedKeysProps> = ({ onBannerUpdate, onNamedKeysChange } ) => {
  const [showAllKeys, setShowAllKeys] = useState(false);
  const [state, setState] = useState<State>({
    namedKeys: [],
    loading: false,
  });

  useEffect(() => {
    fetchNamedKeys();
  }, []);

  const fetchNamedKeys = useCallback(async () => {
    try {
      const response = await apiFetch('Admin/Home/GetNamedKeyCountInfoPackage', {
        method: 'GET'
      });
      
      if (!response.ok) {
        await handleApiError(response);
      }
      const data = await response.json();
      setState(prevState => ({
        ...prevState,
        namedKeys: data.namedKeyCountInfoPackage?.namedKeys ?? [],
        loading: false
      }));
      onNamedKeysChange(data.namedKeyCountInfoPackage?.namedKeys?.length ?? 0);
    } catch (err) {
      console.error('Error fetching named keys data:', err);
      onBannerUpdate({ type: 'danger', message: 'Error fetching named keys data: ' + err });
      setState(prevState => ({
        ...prevState,
        loading: false
      }));
    }
  }, [onNamedKeysChange])

  const toggleShowAllKeys = () => setShowAllKeys(!showAllKeys);

  const handleActivate = async (pendingIds: string[], keyName: string) => {
    try {
      const response = await apiFetch('Admin/Home/NamedKeyActivate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          KeyName: keyName,
          pendingKeyIds: pendingIds
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        onBannerUpdate({ type: 'danger', message: data.error || data.message || 'Failed to activate key' });
        throw new Error(data.error || data.message || 'Failed to activate key');
      }
      onBannerUpdate({ type: 'success', message: data.message || 'Key activated successfully' });
      await fetchNamedKeys();
      
    } catch (error) {
      console.error('Error activating key:', error);
      onBannerUpdate({ type: 'danger', message: 'Error activating key: ' + error });
    }
  };

  return (
    <div>
    <Table>
      <Table.Row _style={{ paddingTop: '0' }}>
        <Table.Cell>
          {state.namedKeys.length}
        </Table.Cell>
      </Table.Row>
      <Table.Row _style={{ paddingTop: '0' }}>
        <Table.Cell>
        <Button 
              kind="secondary" 
              text={showAllKeys ? "Hide all keys" : "Show all keys"} 
              onClick={toggleShowAllKeys}
            />
        </Table.Cell>
      </Table.Row>
    </Table>

    {showAllKeys && (
      <Table columnSettings={[
        { alignment: 'left', width: '6.25rem' },
        { alignment: 'left', width: '3.125rem' },
        { alignment: 'left', width: '3.125rem' },
        { alignment: 'left', width: '3.125rem' },
        { alignment: 'left', width: '3.125rem' },
        { alignment: 'left', width: '3.125rem' },
        { alignment: 'left', width: '3.125rem' },
        { alignment: 'left', width: '3.125rem' }
      ]}>
        <Table.Header>
          <Table.Row>
              <Table.HeaderCell text="Name" />
              <Table.HeaderCell text="Key Type" />
              <Table.HeaderCell text="Pending" />
              <Table.HeaderCell text="Current" />
              <Table.HeaderCell text="Active" />
              <Table.HeaderCell text="Action" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
            {state.namedKeys.map(key => (
              <Table.Row key={key.name}>
                <Table.Cell>{key.name}</Table.Cell>
                <Table.Cell>
                  {key.keyType === 0 ? 'aes256v1' : key.keyType === 1 ? 'rsa2048v1' : key.keyType}
                </Table.Cell> 
                <Table.Cell>{key.pendingCount}</Table.Cell>
                <Table.Cell>{key.currentCount}</Table.Cell>
                <Table.Cell>{key.activeCount}</Table.Cell>
                <Table.Cell _style={{ padding: '0' }}>
                  {key.pendingCount > 0 && (
                    <Button
                      kind="primary"
                      text="Activate"
                      onClick={() => handleActivate(key.pendingIds, key.name)}
                    />
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
        </Table.Body>
      </Table>
    )}
    </div>
  );
};

export default NamedKeysInternal;

